import Card from "components/card/Card";
import Checkbox from "components/form/checkbox/Checkbox";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input";
import Page from "components/page/Page";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Select } from "components/select/index";
import { ICategories, IDepartments, IProduct, TProductVendor } from "redux/reducers/models/product";
import axios, { CancelToken } from "axios";
import { GetVendor, PostVendor } from "api/rpc/2024-04/facilityAdmin/product/vendor";
import { StatusCode } from "api/protocols";
import { showError, showSuccess } from "redux/actions/ui";
import { capitalize, errorMessage, valueToString } from "helpers/Helpers";
import ReactDOM from "react-dom";
import validator from "validator";
import useModal from "hooks/modals/useModal";
import Portal from "elements/Portal";
import Sheet from "components/sheet/Sheet";
import { DeletePurchaseOrder, DeletePurchaseOrderItem, GetPurchaseOrder, GetPurchaseOrderDownload, PostPurchaseOrderItems, PutPurchaseOrder, PutPurchaseOrderClose, PutPurchaseOrderItems } from "api/rpc/2024-04/facilityAdmin/order/purchaseOrder";
import { useHistory, useParams } from "react-router";
import { isEqualWith, isNull } from "lodash";
import AddFolderProductsModal from "components/folders/AddFolderProductsModal";
import { IProductPickerState, normalizeLoadedVariants, variantsFromSelectedProducts } from "../../settings/teesheets/pricingSheets/pricingSheetApplications";
import { defaultBrowseFilter, IBrowseFilter } from "components/folders/productBrowseFilters";
import { appendUniqueProducts, buildSelectAllProductBatch, fetchFolderProductSearchPage, IFolderProductSearchContext } from "components/folders/folderProductSearch";
import { GetProduct } from "api/rpc/2024-04/facilityAdmin/product/product";
import { GetDepartments } from "api/rpc/2024-04/facilityAdmin/facility/department";
import { IPurchaseOrderItem } from "redux/reducers/models/purchaseOrder";
import { faEllipsisVertical } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { NavigationDropdownNew } from "components/navigationDropdownNew/NavigationDropdownNew";
import Popup from "components/popup/Popup";
import { Badge } from "components/badge/Badge";
import TextField from "components/form/textField/TextField";
import useCurrencyFacility from "hooks/useCurrency/useCurrencyFacility";
import Callout from "components/callout/Callout";
import PurchaseOrderPageSkeleton from "./PurchaseOrderPageSkeleton";
import moment from "moment";

interface IState {
    id: number;
    // Billing
    bill_to_name: string;
    bill_to_address_line: string;
    bill_to_city: string;
    bill_to_province: string;
    bill_to_postal: string;
    bill_to_country: string;

    // Shipping
    ship_to_name: string;
    ship_to_address_line: string;
    ship_to_city: string;
    ship_to_province: string;
    ship_to_postal: string;
    ship_to_country: string;

    // Vendor
    vendor_id: number;
    vendor_name: string;
    vendor_address_line: string;
    vendor_city: string;
    vendor_province: string;
    vendor_postal: string;
    vendor_country: string;
    vendor_email: string;
    vendor_phone: string;
    vendor_contact_name: string;
    po_number: string;
    status: string;
    items?: Array<IPurchaseOrderItem>;
    notes: string;
    finalized_at: string;
}

const ITEMS_SELECT_ALL_LIMIT = 200;

type TBrowseFilterState = {
    search: string;
    browseFilter: IBrowseFilter;
};

type TBrowseDepartment = IDepartments & { cateogries?: ICategories[] };

export default function PurchaseOrderEdit() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { facilityStore } = useAppSelector(store => store);
    const { getCurrencySymbol } = useCurrencyFacility();
    const { id } = useParams<{ id: string }>();
    const history = useHistory();
    const [billingSame, setBillingSame] = useState<boolean>(true);
    const [vendorSearch, setVendorSearch] = useState<string>("");
    const [vendors, setVendors] = useState<Array<TProductVendor>>([]);
    const [originalVendor, setOriginalVendor] = useState<TProductVendor>(null);
    const [selectedVendor, setSelectedVendor] = useState<TProductVendor>(null);

    const { state: newVendorModal, updateModal: updateNewVendorModal, closeModal: closeNewVendorModal } = useModal<{ title: string; address_line: string; city: string; province: string; country: string; postal_code: string; email: string; phone: string; contact_name: "" }>({
        title: "",
        address_line: "",
        city: "",
        province: "",
        country: "",
        postal_code: "",
        email: "",
        phone: "",
        contact_name: "",
    });

    const { state: editVendorModal, updateModal: updateEditVendorModal, closeModal: closeEditVendorModal } = useModal<{ vendor_name: string; vendor_address_line: string; vendor_city: string; vendor_province: string; vendor_country: string; vendor_postal: string; vendor_email: string; vendor_phone: string; vendor_contact_name: string }>({
        vendor_name: "",
        vendor_address_line: "",
        vendor_city: "",
        vendor_province: "",
        vendor_country: "",
        vendor_postal: "",
        vendor_email: "",
        vendor_phone: "",
        vendor_contact_name: "",
    });

    const { state: editShipToModal, updateModal: updateShipToModal, closeModal: closeShipToModal } = useModal<{ ship_to_name: string; ship_to_address_line: string; ship_to_city: string; ship_to_province: string; ship_to_country: string; ship_to_postal: string }>({
        ship_to_name: "",
        ship_to_address_line: "",
        ship_to_city: "",
        ship_to_province: "",
        ship_to_country: "",
        ship_to_postal: "",
    });

    const { state: editBillToModal, updateModal: updateBillToModal, closeModal: closeBillToModal } = useModal<{ bill_to_name: string; bill_to_address_line: string; bill_to_city: string; bill_to_province: string; bill_to_country: string; bill_to_postal: string }>({
        bill_to_name: "",
        bill_to_address_line: "",
        bill_to_city: "",
        bill_to_province: "",
        bill_to_country: "",
        bill_to_postal: "",
    });

    const { state: editNotesModal, updateModal: updateNotesModal, closeModal: closeNotesModal } = useModal<{ notes: string }>({
        notes: "",
    });

    const { state: removeItemModal, updateModal: updateRemoveItemModal, closeModal: closeRemoveItemModal } = useModal<{ itemId: number }>({
        itemId: null,
    });

    const { state: closeModal, updateModal: updateCloseModal, closeModal: closeCloseModal } = useModal();

    const { state: statusModal, updateModal: updateStatusModal, closeModal: closeStatusModal } = useModal<{ status: string }>({ status: "" });

    const { state: deleteModal, updateModal: updateDeleteModal, closeModal: closeDeleteModal } = useModal();

    const [productState, setProductState] = useState<IProductPickerState>({
        addProductVisibility: false,
        selectedProducts: [],
        addedVariants: [],
        productSearchResults: undefined,
        productSearchTotalCount: undefined,
        productSearchPending: false,
        productSearchLoadingMore: false,
        productSearchSelectAllPending: false,
    });

    const [browseFilterState, setBrowseFilterState] = useState<TBrowseFilterState>({
        search: "",
        browseFilter: defaultBrowseFilter,
    });

    const [browseDepartments, setBrowseDepartments] = useState<TBrowseDepartment[]>([]);
    const [browseMetadataLoading, setBrowseMetadataLoading] = useState(false);
    const [uncategorizedCount, setUncategorizedCount] = useState(0);
    const browseFilterRef = useRef(browseFilterState.browseFilter);
    const prevAddProductsModalOpen = useRef(false);
    const productSearchCancelTokenRef = useRef<ReturnType<typeof axios.CancelToken.source> | null>(null);
    const [vendorFilterEnabled, setVendorFilterEnabled] = useState(true);

    const { Option } = Select;
    const [state, setState] = useState<IState>({
        id: null,
        // Billing
        bill_to_name: "",
        bill_to_address_line: "",
        bill_to_city: "",
        bill_to_province: "",
        bill_to_postal: "",
        bill_to_country: "",

        // Shipping
        ship_to_name: "",
        ship_to_address_line: "",
        ship_to_city: "",
        ship_to_province: "",
        ship_to_postal: "",
        ship_to_country: "",

        // Vendor
        vendor_id: null,
        vendor_name: "",
        vendor_address_line: "",
        vendor_city: "",
        vendor_province: "",
        vendor_postal: "",
        vendor_country: "",
        vendor_email: "",
        vendor_phone: "",
        vendor_contact_name: "",
        po_number: "",
        status: "",
        items: [],
        notes: "",
        finalized_at: "",
    });

    const [beforeState, setBeforeState] = useState<IState>(undefined);

    const excludedVariantIds = useMemo(
        () => productState.addedVariants.map(variant => variant.id).filter((id): id is number => id != null),
        [productState.addedVariants],
    );

    const excludedVariantIdSet = useMemo(() => new Set(excludedVariantIds), [excludedVariantIds]);

    const billTo = {
        name: state.bill_to_name,
        address_line: state.bill_to_address_line,
        city: state.bill_to_city,
        province: state.bill_to_province,
        postal: state.bill_to_postal,
        country: state.bill_to_country,
    };

    const shipTo = {
        name: state.ship_to_name,
        address_line: state.ship_to_address_line,
        city: state.ship_to_city,
        province: state.ship_to_province,
        postal: state.ship_to_postal,
        country: state.ship_to_country,
    };

    useEffect(() => {
        const source = axios.CancelToken.source();
        void getPurchaseOrder(source.token);
        return () => source.cancel();
    }, []);

    useEffect(() => {
        const source = axios.CancelToken.source();

        let timeoutId: NodeJS.Timeout = null;
        timeoutId = global.setTimeout(() => {
            void searchVendors(source.token, vendorSearch);
        }, 500);

        return () => {
            clearTimeout(timeoutId);
            setVendors([]);
            source.cancel();
        };
    }, [vendorSearch]);

    useEffect(() => {
        if (!productState.addProductVisibility) {
            productSearchCancelTokenRef.current?.cancel();
            setProductState(prevState => ({
                ...prevState,
                productSearchPending: false,
                productSearchLoadingMore: false,
            }));
            prevAddProductsModalOpen.current = false;
            return;
        }

        const justOpened = !prevAddProductsModalOpen.current;
        prevAddProductsModalOpen.current = true;

        const browseChanged =
            browseFilterRef.current.type !== browseFilterState.browseFilter.type ||
            browseFilterRef.current.id !== browseFilterState.browseFilter.id;
        browseFilterRef.current = browseFilterState.browseFilter;
        const delay = browseChanged ? 0 : 1000;

        let mounted = true;
        productSearchCancelTokenRef.current?.cancel();
        const cancelSource = axios.CancelToken.source();
        productSearchCancelTokenRef.current = cancelSource;

        setProductState(prevState => {
            const hasLoadedResults = (prevState.productSearchResults?.length ?? 0) > 0;

            return {
                ...prevState,
                productSearchPending: true,
                productSearchLoadingMore: false,
                ...(hasLoadedResults ? {} : { productSearchResults: undefined, productSearchTotalCount: undefined }),
            };
        });

        const runFetch = async () => {
            try {
                const { products, totalCount } = await fetchFolderProductSearchPage(
                    GetProduct,
                    getProductSearchContext(),
                    0,
                    true,
                    cancelSource.token,
                    () => dispatch(showError(t("Failed to get products"))),
                );
                if (mounted) {
                    setProductState(prevState => ({
                        ...prevState,
                        productSearchResults: products,
                        productSearchTotalCount: totalCount,
                        productSearchPending: false,
                        productSearchLoadingMore: false,
                    }));
                }
            } catch (error) {
                if (axios.isCancel(error)) {
                    return;
                }
                if (mounted) {
                    setProductState(prevState => ({
                        ...prevState,
                        productSearchResults: [],
                        productSearchTotalCount: 0,
                        productSearchPending: false,
                        productSearchLoadingMore: false,
                    }));
                }
            }
        };

        let timeoutId: ReturnType<typeof setTimeout> | undefined;
        if (justOpened) {
            void runFetch();
        } else {
            timeoutId = global.setTimeout(() => {
                void runFetch();
            }, delay);
        }

        return () => {
            mounted = false;
            cancelSource.cancel();
            if (timeoutId !== undefined) {
                clearTimeout(timeoutId);
            }
        };
    }, [browseFilterState.search, browseFilterState.browseFilter, productState.addProductVisibility, vendorFilterEnabled, state?.vendor_id]);

    async function getPurchaseOrder(cancelToken?: CancelToken) {
        if (state?.id !== undefined) {
            ReactDOM.unstable_batchedUpdates(() => {
                setState(prev => ({ ...prev, id: undefined }));
                setBeforeState(() => ({ ...state, id: undefined }));
            });
        }
        const res = await GetPurchaseOrder({ id: Number(id), extended: true }, false, cancelToken);
        if (res?.status !== StatusCode.OK || res?.data?.length === 0) {
            if (cancelToken && cancelToken?.reason) {
                return;
            }

            dispatch(showError(errorMessage(res, t("Error getting purchase order"))));
            return;
        }
        if (res?.data[0]?.vendor_id) {
            const vendorRes = await GetVendor({ id: res?.data[0]?.vendor_id }, false, cancelToken);
            if (vendorRes?.status === StatusCode?.OK && vendorRes?.data?.length > 0) {
                ReactDOM.unstable_batchedUpdates(() => {
                    setSelectedVendor(vendorRes?.data[0]);
                    setVendorSearch(vendorRes?.data[0]?.title ?? "");
                    setOriginalVendor(vendorRes?.data[0]);
                });
            }
        }
        const data = res.data[0];

        const billTo = {
            name: data.bill_to_name,
            address_line: data.bill_to_address_line,
            city: data.bill_to_city,
            province: data.bill_to_province,
            postal: data.bill_to_postal,
            country: data.bill_to_country,
        };

        const shipTo = {
            name: data.ship_to_name,
            address_line: data.ship_to_address_line,
            city: data.ship_to_city,
            province: data.ship_to_province,
            postal: data.ship_to_postal,
            country: data.ship_to_country,
        };

        const variants = data?.items ? data?.items?.map(item => item?.variant) : [];
        const loadedVariants = normalizeLoadedVariants(variants);

        ReactDOM.unstable_batchedUpdates(() => {
            setBillingSame(isEqualWith(shipTo, billTo));
            setState({ ...data, notes: data?.notes ?? "" });
            setBeforeState({ ...data });
            setProductState(prev => ({ ...prev, addedVariants: loadedVariants }));
        });

    }

    async function searchVendors(cancelToken: CancelToken, searchQuery: string) {
        if (vendors) {
            setVendors(undefined);
        }
        const vendorRes = await GetVendor({ search: searchQuery }, false, cancelToken);
        if (vendorRes?.status !== StatusCode.OK) {
            setVendors([]);
            if (cancelToken && cancelToken?.reason) {
                return;
            }
            dispatch(showError(errorMessage(vendorRes, t("Error getting vendors"))));
            return;
        }
        setVendors(vendorRes?.data);
    }

    function onVendorEditInputChange(e: React.ChangeEvent<HTMLInputElement>) {
        const { id, value } = e.target;
        updateEditVendorModal({ [id]: value });
    }

    function onShipToEditInputChange(e: React.ChangeEvent<HTMLInputElement>) {
        const { id, value } = e.target;
        updateShipToModal({ [id]: value });
    }

    function onBillToEditInputChange(e: React.ChangeEvent<HTMLInputElement>) {
        const { id, value } = e.target;
        updateBillToModal({ [id]: value });
    }

    function sameShippingCheck(e: React.ChangeEvent<HTMLInputElement>) {
        const { checked } = e.target;
        if (checked) {
            void updateBillToModal({
                isOpen: true,
                bill_to_name: state.ship_to_name,
                bill_to_address_line: state.ship_to_address_line,
                bill_to_city: state.ship_to_city,
                bill_to_province: state.ship_to_province,
                bill_to_postal: state.ship_to_postal,
                bill_to_country: state.ship_to_country,
            });
        }
        setBillingSame(checked);
    }

    function vendorModalInputChange(e: React.ChangeEvent<HTMLInputElement>) {
        const { id, value } = e.target;
        updateNewVendorModal({ [id]: value });
    }

    async function saveNewVendor() {
        const params = {
            ...newVendorModal,
        };

        if (newVendorModal?.email && !validator?.isEmail(newVendorModal?.email)) {
            dispatch(showError(t("The email address format is invalid")));
            return;
        }

        const res = await PostVendor(params, true);
        if (res.status !== StatusCode.OK) {
            dispatch(showError(errorMessage(res, t("Error creating vendor"))));
            return;
        }

        dispatch(showSuccess(t("Successfully created vendor")));
        const vendor = res?.data;
        ReactDOM.unstable_batchedUpdates(() => {
            setVendorSearch(vendor?.title);
            updateEditVendorModal({
                isOpen: true,
                vendor_name: vendor.title,
                vendor_address_line: vendor.address_line,
                vendor_city: vendor.city,
                vendor_province: vendor.province,
                vendor_postal: vendor.postal_code,
                vendor_country: vendor.country,
                vendor_contact_name: vendor.contact_name,
                vendor_email: vendor.email,
                vendor_phone: vendor.phone,
            });
            setSelectedVendor(vendor);
        });
        void closeNewVendorModal();
    }

    async function updateVendor() {
        if (editVendorModal?.vendor_email && !validator.isEmail(editVendorModal?.vendor_email)) {
            dispatch(showError(t("The email address format is invalid")));
            return;
        }
        const res = await PutPurchaseOrder({
            id: Number(id),
            vendor_name: editVendorModal.vendor_name,
            vendor_address_line: editVendorModal.vendor_address_line,
            vendor_city: editVendorModal.vendor_city,
            vendor_province: editVendorModal.vendor_province,
            vendor_postal: editVendorModal.vendor_postal,
            vendor_country: editVendorModal.vendor_country,
            vendor_contact_name: editVendorModal.vendor_contact_name || null,
            vendor_email: editVendorModal.vendor_email || null,
            vendor_phone: editVendorModal.vendor_phone || null,
            vendor_id: selectedVendor?.id ?? null,
        }, true);

        if (res?.status !== StatusCode.OK) {
            dispatch(showError(errorMessage(res, t("Error updating purchase order"))));
            return;
        }
        void closeEditVendorModal();
        dispatch(showSuccess(t("Successfully updated purchase order")));
        await getPurchaseOrder();
    }

    async function updateShipTo() {
        const res = await PutPurchaseOrder({
            id: Number(id),
            ship_to_name: editShipToModal.ship_to_name,
            ship_to_address_line: editShipToModal.ship_to_address_line,
            ship_to_city: editShipToModal.ship_to_city,
            ship_to_province: editShipToModal.ship_to_province,
            ship_to_postal: editShipToModal.ship_to_postal,
            ship_to_country: editShipToModal.ship_to_country,
        }, true);

        if (res?.status !== StatusCode.OK) {
            dispatch(showError(errorMessage(res, t("Error updating purchase order"))));
            return;
        }
        void closeShipToModal();
        dispatch(showSuccess(t("Successfully updated purchase order")));
        await getPurchaseOrder();
    }

    async function updateBillTo() {
        const res = await PutPurchaseOrder({
            id: Number(id),
            bill_to_name: editBillToModal.bill_to_name,
            bill_to_address_line: editBillToModal.bill_to_address_line,
            bill_to_city: editBillToModal.bill_to_city,
            bill_to_province: editBillToModal.bill_to_province,
            bill_to_postal: editBillToModal.bill_to_postal,
            bill_to_country: editBillToModal.bill_to_country,
        }, true);

        if (res?.status !== StatusCode.OK) {
            dispatch(showError(errorMessage(res, t("Error updating purchase order"))));
            return;
        }
        void closeBillToModal();
        dispatch(showSuccess(t("Successfully updated purchase order")));
        await getPurchaseOrder();
    }

    function handleAddProductsBrowseSearchChange(value: string) {
        setBrowseFilterState(prevState => ({ ...prevState, search: value }));
        setProductState(prev => ({ ...prev, productSearchPending: true }));
    }

    function handleAddProductsBrowseFilterChange(browseFilter: IBrowseFilter) {
        setBrowseFilterState(prevState => ({ ...prevState, browseFilter }));
        setProductState(prev => ({ ...prev, productSearchPending: true }));
    }

    async function loadMoreProducts() {
        let shouldLoad = false;
        let offset = 0;

        setProductState(prev => {
            if (
                prev.productSearchLoadingMore ||
                prev.productSearchPending ||
                !prev.productSearchResults ||
                prev.productSearchTotalCount == null ||
                prev.productSearchResults.length >= prev.productSearchTotalCount
            ) {
                return prev;
            }

            shouldLoad = true;
            offset = prev.productSearchResults.length;
            return { ...prev, productSearchLoadingMore: true };
        });

        if (!shouldLoad) {
            return;
        }

        try {
            const { products } = await fetchFolderProductSearchPage(
                GetProduct,
                getProductSearchContext(),
                offset,
                false,
                undefined,
                () => dispatch(showError(t("Failed to get products"))),
            );
            setProductState(prev => ({
                ...prev,
                productSearchResults: appendUniqueProducts(prev.productSearchResults ?? [], products),
                productSearchLoadingMore: false,
            }));
        } catch {
            setProductState(prev => ({ ...prev, productSearchLoadingMore: false }));
        }
    }

    function getProductSearchContext(): IFolderProductSearchContext {
        return {
            search: browseFilterState.search,
            browseFilter: browseFilterState.browseFilter,
            variants: true,
            vendor_id: vendorFilterEnabled ? state?.vendor_id : undefined,
        };
    }

    async function selectAllMatchingProducts() {
        const loaded = productState.productSearchResults ?? [];
        const totalCount = productState.productSearchTotalCount ?? loaded.length;
        const fullyAddedIds = new Set(
            loaded.filter(product => withSelectableVariants(product) == null).map(product => product.id),
        );
        const fullySelectedIds = new Set(
            productState.selectedProducts
                .filter(selected => {
                    const source = loaded.find(product => product.id === selected.id) ?? selected;
                    const available = withSelectableVariants(source);
                    if (available == null) {
                        return true;
                    }

                    const selectedVariantIds = new Set((selected.variants ?? []).map(variant => variant.id));
                    return available.variants.every(variant => selectedVariantIds.has(variant.id));
                })
                .map(product => product.id),
        );
        const excludeIds = new Set<number>([...fullyAddedIds, ...fullySelectedIds]);
        const remaining = Math.max(0, ITEMS_SELECT_ALL_LIMIT - productState.selectedProducts.length);
        const needsRemoteFetch = remaining > 0 && loaded.length < totalCount;

        if (needsRemoteFetch) {
            setProductState(prev => ({ ...prev, productSearchSelectAllPending: true }));
        }

        try {
            const toAdd = await buildSelectAllProductBatch(
                GetProduct,
                getProductSearchContext(),
                loaded,
                totalCount,
                excludeIds,
                remaining,
                () => dispatch(showError(t("Failed to get products"))),
            );

            const selectable = toAdd
                .map(product => withSelectableVariants(product))
                .filter((product): product is IProduct => product != null);

            setProductState(prev => {
                if (selectable.length === 0) {
                    return { ...prev, productSearchSelectAllPending: false };
                }

                return {
                    ...prev,
                    selectedProducts: mergeSelectedProducts(prev.selectedProducts, selectable),
                    productSearchSelectAllPending: false,
                };
            });
        } catch {
            setProductState(prev => ({ ...prev, productSearchSelectAllPending: false }));
        }
    }

    function mergeSelectedProducts(existing: IProduct[], incoming: IProduct[]) {
        const next = [...existing];

        incoming.forEach(product => {
            const index = next.findIndex(selected => selected.id === product.id);
            if (index === -1) {
                next.push(product);
                return;
            }

            const existingVariantIds = new Set((next[index].variants ?? []).map(variant => variant.id));
            const mergedVariants = [...(next[index].variants ?? [])];
            (product.variants ?? []).forEach(variant => {
                if (!existingVariantIds.has(variant.id)) {
                    mergedVariants.push(variant);
                    existingVariantIds.add(variant.id);
                }
            });
            next[index] = { ...next[index], variants: mergedVariants, variant_count: mergedVariants.length };
        });

        return next;
    }

    function withSelectableVariants(product: IProduct): IProduct | null {
        const variants = (product.variants ?? []).filter(variant => !excludedVariantIdSet.has(variant.id));
        if (variants.length === 0) {
            return null;
        }

        return {
            ...product,
            variants,
            variant_count: variants.length,
        };
    }

    function handleSelectProductToAdd(productOrProducts: IProduct | IProduct[]) {
        const products = Array.isArray(productOrProducts) ? productOrProducts : [productOrProducts];
        const selectable = products
            .map(product => withSelectableVariants(product))
            .filter((product): product is IProduct => product != null);

        if (selectable.length === 0) {
            return;
        }

        setProductState(prev => ({
            ...prev,
            selectedProducts: mergeSelectedProducts(prev.selectedProducts, selectable),
        }));
    }

    function handleUnselectProductToAdd(productOrProducts: IProduct | IProduct[]) {
        const products = Array.isArray(productOrProducts) ? productOrProducts : [productOrProducts];

        setProductState(prev => {
            let next = [...prev.selectedProducts];

            products.forEach(incoming => {
                const index = next.findIndex(product => product.id === incoming.id);
                if (index === -1) {
                    return;
                }

                const removeIds = new Set((incoming.variants ?? []).map(variant => variant.id));
                if (removeIds.size === 0) {
                    next = next.filter(product => product.id !== incoming.id);
                    return;
                }

                const remaining = (next[index].variants ?? []).filter(variant => !removeIds.has(variant.id));
                if (remaining.length === 0) {
                    next = next.filter(product => product.id !== incoming.id);
                } else {
                    next[index] = { ...next[index], variants: remaining, variant_count: remaining.length };
                }
            });

            return { ...prev, selectedProducts: next };
        });
    }

    async function loadBrowseMetadata() {
        const showLoading = browseDepartments.length === 0;

        if (showLoading) {
            setBrowseMetadataLoading(true);
        }

        try {
            const [departmentRes, countRes] = await Promise.all([
                GetDepartments({ extended: true }, false),
                GetProduct(
                    {
                        get_count: true,
                        limit: 1,
                        filters: [{ label: "uncategorized", value: [1] }],
                        vendor_id: vendorFilterEnabled ? state?.vendor_id : undefined,
                    },
                    false,
                ),
            ]);

            if (departmentRes.status === StatusCode.OK) {
                setBrowseDepartments((departmentRes.data ?? []).filter(department => department.type === "department"));
            }

            if (countRes.status === StatusCode.OK && countRes.data && !Array.isArray(countRes.data)) {
                const countData = countRes.data as { count?: number };
                setUncategorizedCount(countData.count ?? 0);
            }
        } finally {
            if (showLoading) {
                setBrowseMetadataLoading(false);
            }
        }
    }

    function handleBrowseProductsVisibility() {
        const opening = !productState.addProductVisibility;

        setProductState(prevState => ({
            ...prevState,
            addProductVisibility: opening,
            selectedProducts: [],
            ...(opening
                ? {
                    productSearchPending: true,
                    productSearchResults: undefined,
                    productSearchTotalCount: undefined,
                }
                : {}),
        }));

        if (opening) {
            setVendorFilterEnabled(true);
            setBrowseFilterState({ search: "", browseFilter: defaultBrowseFilter });
            void loadBrowseMetadata();
        } else {
            setBrowseFilterState({ search: "", browseFilter: defaultBrowseFilter });
        }
    }

    async function handleAddProducts(products: IProduct[]) {
        const incomingVariants = variantsFromSelectedProducts(products);

        if (incomingVariants.length === 0) {
            dispatch(showError(t("A product must be selected")));
            return;
        }

        const res = await PostPurchaseOrderItems({ purchase_order_id: Number(id), variants: incomingVariants?.map(variant => ({ variant_id: variant?.id, quantity: 1, unit_cost: variant?.cost != null ? variant?.cost : 0 })) }, true);
        if (res?.status !== StatusCode.OK) {
            dispatch(showError(errorMessage(res, t("Error adding items to purchase order"))));
            return;
        }
        dispatch(showSuccess(t("Successfully added items to purchase order")));
        setProductState(prevState => ({
            ...prevState,
            selectedProducts: [],
            addProductVisibility: false,
        }));
        setBrowseFilterState({ search: "", browseFilter: defaultBrowseFilter });

        await getPurchaseOrder();
    }

    function handleItemInputChange(e: React.ChangeEvent<HTMLInputElement>, index: number) {
        const { id, value } = e.target;
        const updatedItems = [...state.items];
        updatedItems[index] = { ...updatedItems[index], [id]: value };
        setState(prev => ({ ...prev, items: updatedItems }));
    }

    function unsavedChangesExist() {
        if (beforeState === undefined) {
            if (state?.id) {
                setBeforeState({ ...state });
            }
            return false;
        }

        return !isEqualWith(beforeState, state, (originalValue, newValue) => {
            if ((isNull(originalValue) || originalValue === "") && (isNull(newValue) || newValue === "")) {
                return true;
            }
        });
    }

    function cancelUnsavedChanges() {
        setState({ ...beforeState });
    }

    async function updatePurchaseOrderItems() {
        const updatedItems = state?.items?.map(item => ({ id: item.id, quantity: item.quantity, unit_cost: item?.unit_cost, notes: item?.notes }));
        const res = await PutPurchaseOrderItems({ purchase_order_id: Number(id), items: updatedItems }, true);
        if (res?.status !== StatusCode.OK) {
            dispatch(showError(errorMessage(res, t("Error updating items in purchase order"))));
            return;
        }
        dispatch(showSuccess(t("Successfully updated items in purchase order")));
        await getPurchaseOrder();
    }

    async function deletePurchaseOrderItem() {
        const res = await DeletePurchaseOrderItem({ id: removeItemModal?.itemId }, true);
        if (res?.status !== StatusCode.OK) {
            dispatch(showError(errorMessage(res, t("Error removing item in purchase order"))));
            return;
        }
        void closeRemoveItemModal();
        dispatch(showSuccess(t("Successfully removed item in purchase order")));
        await getPurchaseOrder();
    }

    async function updateNotes() {
        const res = await PutPurchaseOrder({
            id: Number(id),
            notes: editNotesModal?.notes,
        }, true);

        if (res?.status !== StatusCode.OK) {
            dispatch(showError(errorMessage(res, t("Error updating purchase order notes"))));
            return;
        }

        void closeNotesModal();
        dispatch(showSuccess(t("Successfully updated purchase order notes")));
        await getPurchaseOrder();
    }

    async function handleClose() {
        const res = await PutPurchaseOrderClose({
            id: Number(id),
        }, true);

        if (res?.status !== StatusCode.OK) {
            dispatch(showError(errorMessage(res, t("Error closing purchase order"))));
            return;
        }

        void closeCloseModal();
        dispatch(showSuccess(t("Successfully closed purchase order")));
        await getPurchaseOrder();
    }

    async function handleUpdateStatus() {
        const res = await PutPurchaseOrder({
            id: Number(id),
            status: statusModal?.status,
        }, true);

        if (res?.status !== StatusCode.OK) {
            dispatch(showError(errorMessage(res, t("Error updating purchase order"))));
            return;
        }

        void closeStatusModal();
        dispatch(showSuccess(t("Successfully updated purchase order")));
        await getPurchaseOrder();
    }

    async function handleDownload() {
        const res = await GetPurchaseOrderDownload({
            id: Number(id),
        }, true);

        if (res?.status !== StatusCode.OK) {
            dispatch(showError(errorMessage(res, t("Error downloading purchase order"))));
            return;
        }

        window.open(String(res.data));
    }

    async function handleDelete() {
        const res = await DeletePurchaseOrder({
            id: Number(id),
        }, true);

        if (res?.status !== StatusCode.OK) {
            dispatch(showError(errorMessage(res, t("Error deleting purchase order"))));
            return;
        }

        void closeDeleteModal();
        dispatch(showSuccess(t("Successfully deleted purchase order")));
        history.push("/admin/settings/purchase-orders");
    }

    const disableEdit = state?.status === "closed" || state?.status === "cancelled";
    const canReceive = state?.status === "ordered" || state?.status === "partially_received";

    return (
        <>

            <Page
                title={`${t("PO Number")}: ${state?.po_number}`}
                splitLayout
                breadcrumbs={[{
                    prefix: true,
                    label: t("Back to purchase orders"),
                    url: "/admin/settings/purchase-orders",
                }]}
                subtitle={state?.finalized_at ? `Closed on ${moment.utc(state?.finalized_at, "YYYY-MM-DD HH:mm:ss")?.local()?.format("LLL")}` : ""}
                primaryAction={!disableEdit ? { content: t("Save"), action: () => updatePurchaseOrderItems(), disabled: state?.id === undefined } : undefined}
                secondaryActions={canReceive ? [{
                    content: t("Receive"),
                    action: () => history.push(`/admin/settings/inventory/new?purchase_order_id=${state.id}`),
                }] : undefined}
                titleMetadata={<Badge type={state?.status === "closed" ? "success" : state?.status === "cancelled" ? "error" : "gray"} size="medium">{valueToString(state?.status)}</Badge>}
                notificationBarProps={{
                    isVisible: unsavedChangesExist(),
                    onAction: () => updatePurchaseOrderItems(),
                    onCancel: cancelUnsavedChanges,
                }}
                multipleActionDropdownAction={{
                    label: t("Options"),
                    dropdownProps: {
                        alignment: "right",
                        options: [
                            {
                                type: "handler",
                                label: t("Download"),
                                icon: "download",
                                handler: () => handleDownload(),
                            },
                            ...(state?.status === "draft" ? [
                                {
                                    type: "handler",
                                    label: t("Mark as Ordered"),
                                    icon: "box",
                                    handler: () => updateStatusModal({ isOpen: true, status: "ordered" }),
                                } as any,
                                {
                                    type: "handler",
                                    label: t("Delete"),
                                    icon: "trash-can",
                                    handler: () => updateDeleteModal({ isOpen: true }),
                                } as any,
                            ] : state?.status === "received" ? [
                                {
                                    type: "handler",
                                    label: t("Close"),
                                    icon: "check",
                                    handler: () => updateCloseModal({ isOpen: true }),
                                } as any,
                            ] : []),
                            ...(state?.status !== "closed" && state?.status !== "cancelled" && state?.status !== "draft" ? [
                                {
                                    type: "handler",
                                    label: t("Cancel"),
                                    icon: "xmark",
                                    handler: () => updateStatusModal({ isOpen: true, status: "cancelled" }),
                                } as any,
                            ] : []),
                        ],
                    },
                }}
            >
                {state?.id === undefined ? (
                    <PurchaseOrderPageSkeleton />
                ) : (
                    <>
                        <Page.Section twoThirds>
                            <Card
                                title={t("Items")}
                                titleActions={[
                                    {
                                        content: t("Add Items"),
                                        action: () => handleBrowseProductsVisibility(),
                                        disabled: disableEdit,
                                    },
                                ]}>
                                <Card.Section>
                                    {state?.items?.map((item, index) => {
                                        return (
                                            <Card.SubSection key={index}>
                                                <div className="flex justify-between items-center">
                                                    <div>
                                                        <p className="text-lg text-semibold">{item.product_name}</p>
                                                        {item.product_name !== item.variant_name ? (
                                                            <p className="text-sm text-gray-500">{item.variant_name}</p>
                                                        ) : null}
                                                        {item?.variant?.sku ? <p className="text-sm text-gray-500">{t("SKU")} - {item?.variant?.sku}</p> : null}
                                                        {item?.variant?.barcode ? <p className="text-sm text-gray-500">{t("Barcode")} - {item?.variant?.barcode}</p> : null}
                                                    </div>
                                                    <div className="flex items-center gap-4">
                                                        <Input
                                                            value={item?.quantity}
                                                            label={t("Quantity")}
                                                            id={"quantity"}
                                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleItemInputChange(e, index)}
                                                            type="number"
                                                            min={1}
                                                            placeholder={"1"}
                                                            disabled={disableEdit}
                                                        />
                                                        {state?.status === "partially_received" || state?.status === "received" || state?.status === "closed" ? (
                                                            <Input
                                                                value={item?.quantity_received}
                                                                label={t("Quantity received")}
                                                                id={"quantity_received"}
                                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleItemInputChange(e, index)}
                                                                type="number"
                                                                readOnly
                                                            />
                                                        ) : null}
                                                        <Input
                                                            value={item?.unit_cost}
                                                            label={t("Unit cost")}
                                                            id={"unit_cost"}
                                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleItemInputChange(e, index)}
                                                            type="number"
                                                            prefix={getCurrencySymbol(facilityStore?.facility?.default_currency_code)}
                                                            min={0}
                                                            disabled={disableEdit}
                                                        />
                                                        {!disableEdit ? (
                                                            <div className="mt-3">
                                                                <NavigationDropdownNew
                                                                    showPlainTextLabel
                                                                    rightAlign
                                                                    label={<FontAwesomeIcon icon={faEllipsisVertical} />}
                                                                    sections={[
                                                                        [
                                                                            {
                                                                                label: t("Remove"),
                                                                                icon: "x",
                                                                                onClick: () => updateRemoveItemModal({ itemId: item?.id, isOpen: true }),
                                                                                disabled: disableEdit,
                                                                            },
                                                                        ],
                                                                    ]}
                                                                />
                                                            </div>
                                                        ) : null}

                                                    </div>
                                                </div>
                                            </Card.SubSection>
                                        );
                                    })}
                                </Card.Section>
                            </Card>
                        </Page.Section>
                        <Page.Section oneThird>
                            <Card>
                                <Card.Section title={t("Vendor")} sectionTitleActions={!disableEdit ? [{
                                    content: t("Edit"), action: () => updateEditVendorModal({
                                        isOpen: true,
                                        vendor_name: state.vendor_name,
                                        vendor_address_line: state.vendor_address_line,
                                        vendor_city: state.vendor_city,
                                        vendor_province: state.vendor_province,
                                        vendor_postal: state.vendor_postal,
                                        vendor_country: state.vendor_country,
                                        vendor_contact_name: state.vendor_contact_name,
                                        vendor_email: state.vendor_email,
                                        vendor_phone: state.vendor_phone,
                                    }),
                                }] : undefined}>
                                    <p className="text-md">{state?.vendor_name}</p>
                                    <p className="text-md">{state?.vendor_address_line}, {state?.vendor_city} {state?.vendor_province} {state?.vendor_country} {state?.vendor_postal}</p>
                                    <p className="text-md">{state?.vendor_email}</p>
                                    <p className="text-md">{state?.vendor_phone}</p>
                                    <p className="text-md">{state?.vendor_contact_name}</p>
                                </Card.Section>
                            </Card>
                            <Card>
                                <Card.Section title={t("Ship To")} sectionTitleActions={!disableEdit ? [{
                                    content: t("Edit"), action: () => updateShipToModal({
                                        isOpen: true,
                                        ship_to_name: state.ship_to_name,
                                        ship_to_address_line: state.ship_to_address_line,
                                        ship_to_city: state.ship_to_city,
                                        ship_to_province: state.ship_to_province,
                                        ship_to_postal: state.ship_to_postal,
                                        ship_to_country: state.ship_to_country,
                                    }),
                                }] : undefined}>
                                    <p className="text-md">{state?.ship_to_name}</p>
                                    <p className="text-md">{state?.ship_to_address_line}, {state?.ship_to_city} {state?.ship_to_province} {state?.ship_to_country} {state?.ship_to_postal}</p>
                                </Card.Section>
                            </Card>
                            <Card>
                                <Card.Section title={t("Bill To")} sectionTitleActions={!disableEdit ? [{
                                    content: t("Edit"), action: () => {
                                        updateBillToModal({
                                            isOpen: true,
                                            bill_to_name: state.bill_to_name,
                                            bill_to_address_line: state.bill_to_address_line,
                                            bill_to_city: state.bill_to_city,
                                            bill_to_province: state.bill_to_province,
                                            bill_to_postal: state.bill_to_postal,
                                            bill_to_country: state.bill_to_country,
                                        });
                                        if (isEqualWith(shipTo, billTo)) {
                                            setBillingSame(true);
                                        } else {
                                            setBillingSame(false);
                                        }
                                    },
                                }] : undefined}>
                                    <p className="text-md">{state?.bill_to_name}</p>
                                    <p className="text-md">{state?.bill_to_address_line}, {state?.bill_to_city} {state?.bill_to_province} {state?.bill_to_country} {state?.bill_to_postal}</p>
                                </Card.Section>
                            </Card>
                            <Card>
                                <Card.Section title={t("Notes")} sectionTitleActions={!disableEdit ? [{
                                    content: t("Edit"), action: () => updateNotesModal({ isOpen: true, notes: state?.notes ?? "" }),
                                }] : undefined}>
                                    {state?.notes ? (
                                        <p className="text-md">{state?.notes}</p>
                                    ) : <Callout title={t("No notes")} type="info" content={t("Click edit to add notes to this purchase order")} />}

                                </Card.Section>
                            </Card>
                        </Page.Section>
                    </>
                )}
            </Page>

            {/* EDIT BILL TO */}
            <Portal isMounted={editBillToModal?.isOpen}>
                <Sheet
                    title={t("Edit bill to")}
                    open={editBillToModal?.isOpen}
                    size="small"
                    stacked
                    onCancel={closeBillToModal}
                    onOk={updateBillTo}
                    okText={t("Update")}
                    closable
                >
                    <FormLayout>
                        <FormLayout.Group>
                            <Checkbox checked={billingSame} onChange={sameShippingCheck} label={t("Same as shipping address")} size="medium" />
                        </FormLayout.Group>
                        <FormLayout.Group>
                            <Input
                                label={t("Facility Name")}
                                value={editBillToModal?.bill_to_name}
                                onChange={onBillToEditInputChange}
                                id="bill_to_name"
                                placeholder={t("Facility Name")}
                                disabled={billingSame}
                            />
                        </FormLayout.Group>

                        <FormLayout.Group>
                            <Input
                                label={t("Address Line")}
                                value={editBillToModal?.bill_to_address_line}
                                onChange={onBillToEditInputChange}
                                id="bill_to_address_line"
                                placeholder={t("Address Line")}
                                disabled={billingSame}
                            />
                        </FormLayout.Group>
                        <FormLayout.Group>
                            <Input
                                label={t("City")}
                                value={editBillToModal?.bill_to_city}
                                onChange={onBillToEditInputChange}
                                id="bill_to_city"
                                placeholder={t("City")}
                                disabled={billingSame}
                            />
                            <Input
                                label={t("Province")}
                                value={editBillToModal?.bill_to_province}
                                onChange={onBillToEditInputChange}
                                id="bill_to_province"
                                placeholder={t("Province")}
                                disabled={billingSame}
                            />
                        </FormLayout.Group>
                        <FormLayout.Group>
                            <Input
                                label={t("Country")}
                                value={editBillToModal?.bill_to_country}
                                onChange={onBillToEditInputChange}
                                id="bill_to_country"
                                placeholder={t("Country")}
                                disabled={billingSame}
                            />
                            <Input
                                label={t("Postal Code")}
                                value={editBillToModal?.bill_to_postal}
                                onChange={onBillToEditInputChange}
                                id="bill_to_postal"
                                placeholder={t("Postal Code")}
                                disabled={billingSame}
                            />
                        </FormLayout.Group>
                    </FormLayout>
                </Sheet>
            </Portal>

            {/* EDIT SHIP TO */}
            <Portal isMounted={editShipToModal?.isOpen}>
                <Sheet
                    title={t("Edit ship to")}
                    open={editShipToModal?.isOpen}
                    size="small"
                    stacked
                    onCancel={closeShipToModal}
                    onOk={updateShipTo}
                    okText={t("Update")}
                    closable
                >
                    <FormLayout>
                        <FormLayout.Group>
                            <Input
                                label={t("Facility Name")}
                                value={editShipToModal?.ship_to_name}
                                onChange={onShipToEditInputChange}
                                id="ship_to_name"
                                placeholder={t("Facility Name")}
                            />
                        </FormLayout.Group>
                        <FormLayout.Group>
                            <Input
                                label={t("Address Line")}
                                value={editShipToModal?.ship_to_address_line}
                                onChange={onShipToEditInputChange}
                                id="ship_to_address_line"
                                placeholder={t("Address Line")}
                            />
                        </FormLayout.Group>
                        <FormLayout.Group>
                            <Input
                                label={t("City")}
                                value={editShipToModal?.ship_to_city}
                                onChange={onShipToEditInputChange}
                                id="ship_to_city"
                                placeholder={t("City")}
                            />
                            <Input
                                label={t("Province")}
                                value={editShipToModal?.ship_to_province}
                                onChange={onShipToEditInputChange}
                                id="ship_to_province"
                                placeholder={t("Province")}
                            />
                        </FormLayout.Group>
                        <FormLayout.Group>
                            <Input
                                label={t("Country")}
                                value={editShipToModal?.ship_to_country}
                                onChange={onShipToEditInputChange}
                                id="ship_to_country"
                                placeholder={t("Country")}
                            />
                            <Input
                                label={t("Postal Code")}
                                value={editShipToModal?.ship_to_postal}
                                onChange={onShipToEditInputChange}
                                id="ship_to_postal"
                                placeholder={t("Postal Code")}
                            />
                        </FormLayout.Group>
                    </FormLayout>
                </Sheet>
            </Portal>

            {/* EDIT VENDOR */}
            <Portal isMounted={editVendorModal?.isOpen}>
                <Sheet
                    title={t("Edit vendor")}
                    open={editVendorModal?.isOpen}
                    size="small"
                    stacked
                    onCancel={() => {
                        void closeEditVendorModal();
                        setSelectedVendor(originalVendor ? { ...originalVendor } : null);
                        setVendorSearch(originalVendor ? originalVendor?.title : "");
                    }}
                    onOk={updateVendor}
                    okText={t("Update")}
                    closable
                >
                    <FormLayout>
                        <FormLayout.Group>
                            <Select
                                label={t("Vendor")}
                                placeholder={t("Vendor")}
                                showSearch
                                onSearch={(query: string) => {
                                    setVendorSearch(query);
                                    if (!query) {
                                        setSelectedVendor(null);
                                    }
                                }}
                                onChange={(_, vendor: TProductVendor) => {
                                    ReactDOM.unstable_batchedUpdates(() => {
                                        setSelectedVendor(vendor);
                                        setVendorSearch(vendor?.title);
                                        if (vendor) {
                                            updateEditVendorModal({
                                                isOpen: true,
                                                vendor_name: vendor.title ?? "",
                                                vendor_address_line: vendor.address_line ?? "",
                                                vendor_city: vendor.city ?? "",
                                                vendor_province: vendor.province ?? "",
                                                vendor_postal: vendor.postal_code ?? "",
                                                vendor_country: vendor.country ?? "",
                                                vendor_contact_name: vendor.contact_name ?? "",
                                                vendor_email: vendor.email ?? "",
                                                vendor_phone: vendor.phone ?? "",
                                            });
                                        }
                                    });
                                }}
                                allowClear
                                searchValue={vendorSearch}
                                showDropDownOnFocus={true}
                                searching={vendors === undefined}
                                defaultValue={selectedVendor?.id}
                                // usePortal
                                hideDropdown
                            >
                                <div
                                    className="ui-select-dropdown-list-item"
                                    onClick={() => updateNewVendorModal({ isOpen: true, title: capitalize(vendorSearch ?? "") })}
                                >
                                    <p className="text-md">{t("New")}</p>
                                </div>
                                {vendors?.map((vendor, index) => {
                                    return (
                                        <Option key={index} value={vendor.id} extraValues={vendor}>
                                            {vendor?.title}
                                        </Option>
                                    );
                                })}
                            </Select>
                        </FormLayout.Group>
                    </FormLayout>

                    <FormLayout>
                        <FormLayout.Group>
                            <Input
                                label={t("Vendor Name")}
                                value={editVendorModal?.vendor_name}
                                onChange={onVendorEditInputChange}
                                id="vendor_name"
                                placeholder={t("Vendor Name")}
                            />
                        </FormLayout.Group>
                        <FormLayout.Group>
                            <Input
                                label={t("Address Line")}
                                value={editVendorModal?.vendor_address_line}
                                onChange={onVendorEditInputChange}
                                id="vendor_address_line"
                                placeholder={t("Address Line")}
                            />
                        </FormLayout.Group>
                        <FormLayout.Group>
                            <Input
                                label={t("City")}
                                value={editVendorModal?.vendor_city}
                                onChange={onVendorEditInputChange}
                                id="vendor_city"
                                placeholder={t("City")}
                            />
                            <Input
                                label={t("Province")}
                                value={editVendorModal?.vendor_province}
                                onChange={onVendorEditInputChange}
                                id="vendor_province"
                                placeholder={t("Province")}
                            />
                        </FormLayout.Group>
                        <FormLayout.Group>
                            <Input
                                label={t("Country")}
                                value={editVendorModal?.vendor_country}
                                onChange={onVendorEditInputChange}
                                id="vendor_country"
                                placeholder={t("Country")}
                            />
                            <Input
                                label={t("Postal Code")}
                                value={editVendorModal?.vendor_postal}
                                onChange={onVendorEditInputChange}
                                id="vendor_postal"
                                placeholder={t("Postal Code")}
                            />
                        </FormLayout.Group>
                    </FormLayout>

                    <FormLayout>
                        <FormLayout.Group>
                            <Input
                                label={t("Email")}
                                value={editVendorModal?.vendor_email}
                                onChange={onVendorEditInputChange}
                                id="vendor_email"
                                placeholder={t("Email")}
                                type="email"
                                error={editVendorModal?.vendor_email && !validator.isEmail(editVendorModal?.vendor_email)}
                            />
                            <Input
                                label={t("Phone")}
                                value={editVendorModal?.vendor_phone}
                                onChange={onVendorEditInputChange}
                                id="vendor_phone"
                                placeholder={t("Phone")}
                                type="number"
                            />
                        </FormLayout.Group>
                        <FormLayout.Group>
                            <Input
                                label={t("Contact Name")}
                                value={editVendorModal?.vendor_contact_name}
                                onChange={onVendorEditInputChange}
                                id="vendor_contact_name"
                                placeholder={t("Contact Name")}
                            />
                        </FormLayout.Group>
                    </FormLayout>
                </Sheet>
            </Portal>

            <Portal isMounted={editNotesModal?.isOpen}>
                <Sheet
                    title={t("Notes")}
                    open={editNotesModal?.isOpen}
                    size="small"
                    stacked
                    onCancel={closeNotesModal}
                    onOk={updateNotes}
                    okText={t("Save")}
                    closable
                >
                    <TextField rows={5} label={t("Notes")} placeholder={t("Add notes...")} maxLength={255} characterCountBottom onChange={e => updateNotesModal({ notes: e.target.value })} value={editNotesModal?.notes ?? ""} />
                </Sheet>
            </Portal>

            {/* NEW VENDOR */}
            <Portal isMounted={newVendorModal?.isOpen}>
                <Sheet
                    title={t("New vendor")}
                    open={newVendorModal?.isOpen}
                    size="small"
                    stacked
                    onCancel={closeNewVendorModal}
                    onOk={saveNewVendor}
                    okText={t("Create")}
                    closable
                >
                    <FormLayout>
                        <FormLayout.Group>
                            <Input
                                value={newVendorModal.title}
                                label={t("Name")}
                                id="title"
                                onChange={vendorModalInputChange}
                                placeholder={t("Name")}
                            />
                        </FormLayout.Group>
                        <FormLayout.Group>
                            <Input
                                value={newVendorModal.address_line}
                                label={t("Address")}
                                id="address_line"
                                onChange={vendorModalInputChange}
                                placeholder={t("Address")}
                            />
                        </FormLayout.Group>
                        <FormLayout.Group>
                            <Input
                                value={newVendorModal.city}
                                label={t("City")}
                                id="city"
                                onChange={vendorModalInputChange}
                                placeholder={t("City")}
                            />
                            <Input
                                value={newVendorModal.province}
                                label={t("Province")}
                                id="province"
                                onChange={vendorModalInputChange}
                                placeholder={t("Province")}
                            />
                        </FormLayout.Group>
                        <FormLayout.Group>
                            <Input
                                value={newVendorModal.country}
                                label={t("Country")}
                                id="country"
                                onChange={vendorModalInputChange}
                                placeholder={t("Country")}
                            />
                            <Input
                                value={newVendorModal.postal_code}
                                label={t("Postal Code")}
                                id="postal_code"
                                onChange={vendorModalInputChange}
                                placeholder={t("Postal Code")}
                            />
                        </FormLayout.Group>
                        <FormLayout.Group>
                            <Input
                                value={newVendorModal.email}
                                label={t("Email")}
                                id="email"
                                type="email"
                                onChange={vendorModalInputChange}
                                placeholder={t("Email")}
                                error={newVendorModal?.email && !validator.isEmail(newVendorModal?.email)}
                            />
                            <Input
                                value={newVendorModal.phone}
                                label={t("Phone")}
                                id="phone"
                                type="number"
                                onChange={vendorModalInputChange}
                                placeholder={t("Phone")}
                                error={newVendorModal?.phone && !validator?.isNumeric(newVendorModal?.phone)}
                            />
                        </FormLayout.Group>
                        <FormLayout.Group>
                            <Input
                                value={newVendorModal.contact_name}
                                label={t("Contact Name")}
                                id="contact_name"
                                onChange={vendorModalInputChange}
                                placeholder={t("Contact Name")}
                            />
                        </FormLayout.Group>
                    </FormLayout>
                </Sheet>
            </Portal>

            <Portal isMounted={closeModal?.isOpen}>
                <Popup open={closeModal?.isOpen} type="warning" okText={t("Confirm")} title={t("Close")} description={t("Are you sure you want to close the purchase order? The purchase order will no longer be editable. This cannot be undone.")} onOk={handleClose} onCancel={closeCloseModal} />
            </Portal>

            <Portal isMounted={statusModal?.isOpen && statusModal?.status !== "cancelled"}>
                <Popup open={statusModal?.isOpen && statusModal?.status !== "cancelled"} type="info" okText={t("Confirm")} title={t("Mark Purchase Order as Ordered")} description={t("Are you sure you want to mark this purchase order as {{status}}? It will be available to select when receiving shipments.", { status: statusModal?.status })} onOk={handleUpdateStatus} onCancel={closeStatusModal} />
            </Portal>

            <Portal isMounted={statusModal?.isOpen && statusModal?.status === "cancelled"}>
                <Popup open={statusModal?.isOpen && statusModal?.status === "cancelled"} type="warning" okText={t("Confirm")} title={t("Cancel Purchase Order")} description={t("Are you sure you want to cancel this purchase order?")} onOk={handleUpdateStatus} onCancel={closeStatusModal} />
            </Portal>

            <Portal isMounted={deleteModal?.isOpen}>
                <Popup open={deleteModal?.isOpen} type="warning" okText={t("Delete")} title={t("Delete Purchase Order")} description={t("Are you sure you want to delete the purchase order? This cannot be undone.")} onOk={handleDelete} onCancel={closeDeleteModal} />
            </Portal>

            <AddFolderProductsModal
                open={productState.addProductVisibility}
                onClose={handleBrowseProductsVisibility}
                onAdd={handleAddProducts}
                search={browseFilterState.search}
                onSearchChange={handleAddProductsBrowseSearchChange}
                browseFilter={browseFilterState.browseFilter}
                onBrowseFilterChange={handleAddProductsBrowseFilterChange}
                departments={browseDepartments}
                departmentsLoading={browseMetadataLoading}
                uncategorizedCount={uncategorizedCount}
                productsToAdd={productState.selectedProducts}
                availableSlotCount={ITEMS_SELECT_ALL_LIMIT}
                totalEmptySlotCount={ITEMS_SELECT_ALL_LIMIT}
                folderProducts={[]}
                productSearchResults={productState.productSearchResults}
                productSearchTotalCount={productState.productSearchTotalCount}
                productSearchPending={productState.productSearchPending}
                productSearchLoadingMore={productState.productSearchLoadingMore}
                productSearchSelectAllPending={productState.productSearchSelectAllPending}
                onLoadMoreProducts={loadMoreProducts}
                onSelectAllMatchingProducts={selectAllMatchingProducts}
                onSelectProduct={handleSelectProductToAdd}
                onUnselectProduct={handleUnselectProductToAdd}
                hideSlots
                enableVariantSelection
                excludedVariantIds={excludedVariantIds}
                title={t("Browse products")}
                supportText={t("Add items to this purchase order")}
                onCreateProduct={() => {
                    void handleBrowseProductsVisibility();
                    history.push("/admin/product/new");
                }}
                onGoToProducts={() => {
                    void handleBrowseProductsVisibility();
                    history.push("/admin/product");
                }}
                vendorId={state?.vendor_id}
                vendorFilterEnabled={vendorFilterEnabled}
                onVendorFilterChange={setVendorFilterEnabled}
            />

            <Portal isMounted={removeItemModal?.isOpen}>
                <Popup type="warning" open={removeItemModal?.isOpen} title={t("Remove Item")} description={t("Are you sure you want to remove this item from the purchase order?")} onCancel={closeRemoveItemModal} onOk={deletePurchaseOrderItem} okText={t("Remove")} />
            </Portal>
        </>
    );
}